import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./DropdownWithSearch.module.css";

type Props = {
  trigger: React.ReactNode;
  children: React.ReactNode;
  searchBar?: SearchBarProps;
  isOpen?: boolean;
  onUserDismiss: () => void;
  matchTriggerWidth?: boolean;
};

type SearchBarProps = {
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function SearchBar({ placeholder, value, onChange }: SearchBarProps) {
  return (
    <div className={styles.searchBarContent}>
      <input
        placeholder={placeholder}
        className={styles.searchInput}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

export function DropdownWithSearch({
  trigger,
  children,
  searchBar,
  isOpen,
  onUserDismiss,
  matchTriggerWidth = false,
}: Props) {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Calculate position relative to trigger
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      // use viewport-relative coordinates for fixed positioning
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!triggerRef.current || !contentRef.current) return;
      if (triggerRef.current.contains(e.target as Node)) return;
      if (contentRef.current.contains(e.target as Node)) return;
      onUserDismiss();
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onUserDismiss();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onUserDismiss]);

  const dropdownContent = isOpen ? (
    <div
      className={styles.dropdownContentWrapper}
      ref={contentRef}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: matchTriggerWidth ? position.width : undefined,
      }}
    >
      {searchBar && (
        <SearchBar
          placeholder={searchBar.placeholder}
          value={searchBar.value}
          onChange={searchBar.onChange}
        />
      )}
      <div className={styles.popoverContent}>
        <div
          className={`${styles.dropdownContentScrollable}${searchBar ? ` ${styles.dropdownContentScrollableWithDivider}` : ""}`}
        >
          {children}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={styles.dropdownRoot}>
      <div ref={triggerRef}>{trigger}</div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
